import { findClosestSwatchIndex, getSwatch, swatchByMode } from "./palette";
import { colorRecipeFactory, designSystemResolverMax, } from "./common";
import { baseLayerLuminance, neutralFillActiveDelta, neutralFillCardDelta, neutralFillHoverDelta, neutralFillRestDelta, neutralPalette, } from "../design-system";
import { add, multiply, subtract } from "@microsoft/fast-jss-utilities";
import { clamp, ColorRGBA64 } from "@microsoft/fast-colors";
/**
 * @deprecated Use the recipes because they can be more dynamic for different ramps
 */
export var NeutralPaletteLightModeLayers;
(function (NeutralPaletteLightModeLayers) {
    NeutralPaletteLightModeLayers[NeutralPaletteLightModeLayers["L1"] = 0] = "L1";
    NeutralPaletteLightModeLayers[NeutralPaletteLightModeLayers["L1Alt"] = 3] = "L1Alt";
    NeutralPaletteLightModeLayers[NeutralPaletteLightModeLayers["L2"] = 10] = "L2";
    NeutralPaletteLightModeLayers[NeutralPaletteLightModeLayers["L3"] = 13] = "L3";
    NeutralPaletteLightModeLayers[NeutralPaletteLightModeLayers["L4"] = 16] = "L4";
})(NeutralPaletteLightModeLayers || (NeutralPaletteLightModeLayers = {}));
/**
 * @deprecated Use the recipes because they can be more dynamic for different ramps
 */
export var NeutralPaletteDarkModeLayers;
(function (NeutralPaletteDarkModeLayers) {
    NeutralPaletteDarkModeLayers[NeutralPaletteDarkModeLayers["L1"] = 76] = "L1";
    NeutralPaletteDarkModeLayers[NeutralPaletteDarkModeLayers["L1Alt"] = 76] = "L1Alt";
    NeutralPaletteDarkModeLayers[NeutralPaletteDarkModeLayers["L2"] = 79] = "L2";
    NeutralPaletteDarkModeLayers[NeutralPaletteDarkModeLayers["L3"] = 82] = "L3";
    NeutralPaletteDarkModeLayers[NeutralPaletteDarkModeLayers["L4"] = 85] = "L4";
})(NeutralPaletteDarkModeLayers || (NeutralPaletteDarkModeLayers = {}));
/**
 * Recommended values for light and dark mode for `baseLayerLuminance` in the design system.
 */
export var StandardLuminance;
(function (StandardLuminance) {
    StandardLuminance[StandardLuminance["LightMode"] = 1] = "LightMode";
    StandardLuminance[StandardLuminance["DarkMode"] = 0.23] = "DarkMode";
})(StandardLuminance || (StandardLuminance = {}));
function luminanceOrBackgroundColor(luminanceRecipe, backgroundRecipe) {
    return (designSystem) => {
        return baseLayerLuminance(designSystem) === -1
            ? backgroundRecipe(designSystem)
            : luminanceRecipe(designSystem);
    };
}
/**
 * Find the palette color that's closest to the desired base layer luminance.
 */
const baseLayerLuminanceSwatch = (designSystem) => {
    const luminance = baseLayerLuminance(designSystem);
    return new ColorRGBA64(luminance, luminance, luminance, 1).toStringHexRGB();
};
/**
 * Get the index of the base layer palette color.
 */
const baseLayerLuminanceIndex = findClosestSwatchIndex(neutralPalette, baseLayerLuminanceSwatch);
/**
 * Get the actual value of the card layer index, clamped so we can use it to base other layers from.
 */
const neutralLayerCardIndex = (designSystem) => clamp(subtract(baseLayerLuminanceIndex, neutralFillCardDelta)(designSystem), 0, neutralPalette(designSystem).length - 1);
/**
 * Light mode L2 is significant because it happens at the same point as the neutral fill flip. Use this as the minimum index for L2.
 */
const lightNeutralLayerL2 = designSystemResolverMax(neutralFillRestDelta, neutralFillHoverDelta, neutralFillActiveDelta);
/**
 * The index for L2 based on luminance, adjusted for the flip in light mode if necessary.
 */
const neutralLayerL2Index = designSystemResolverMax(add(baseLayerLuminanceIndex, neutralFillCardDelta), lightNeutralLayerL2);
/**
 * Dark mode L4 is the darkest recommended background in the standard guidance, which is
 * calculated based on luminance to work with variable sized ramps.
 */
const darkNeutralLayerL4 = (designSystem) => {
    const darkLum = 0.14;
    const darkColor = new ColorRGBA64(darkLum, darkLum, darkLum, 1);
    const darkRefIndex = findClosestSwatchIndex(neutralPalette, darkColor.toStringHexRGB())(designSystem);
    return darkRefIndex;
};
/**
 * Used as the background color for floating layers like context menus and flyouts.
 */
export const neutralLayerFloating = colorRecipeFactory(luminanceOrBackgroundColor(getSwatch(subtract(neutralLayerCardIndex, neutralFillCardDelta), neutralPalette), swatchByMode(neutralPalette)(0, subtract(darkNeutralLayerL4, multiply(neutralFillCardDelta, 5)))));
/**
 * Used as the background color for cards. Pair with `neutralLayerCardContainer` for the container background.
 */
export const neutralLayerCard = colorRecipeFactory(luminanceOrBackgroundColor(getSwatch(neutralLayerCardIndex, neutralPalette), swatchByMode(neutralPalette)(0, subtract(darkNeutralLayerL4, multiply(neutralFillCardDelta, 4)))));
/**
 * Used as the background color for card containers. Pair with `neutralLayerCard` for the card backgrounds.
 */
export const neutralLayerCardContainer = colorRecipeFactory(luminanceOrBackgroundColor(getSwatch(add(neutralLayerCardIndex, neutralFillCardDelta), neutralPalette), swatchByMode(neutralPalette)(neutralFillCardDelta, subtract(darkNeutralLayerL4, multiply(neutralFillCardDelta, 3)))));
/**
 * Used as the background color for the primary content layer (L1).
 */
export const neutralLayerL1 = colorRecipeFactory(luminanceOrBackgroundColor(getSwatch(baseLayerLuminanceIndex, neutralPalette), swatchByMode(neutralPalette)(0, subtract(darkNeutralLayerL4, multiply(neutralFillCardDelta, 3)))));
/**
 * Alternate darker color for L1 surfaces. Currently the same as card container, but use
 * the most applicable semantic named recipe.
 */
export const neutralLayerL1Alt = neutralLayerCardContainer;
/**
 * Used as the background for the top command surface, logically below L1.
 */
export const neutralLayerL2 = colorRecipeFactory(luminanceOrBackgroundColor(getSwatch(neutralLayerL2Index, neutralPalette), swatchByMode(neutralPalette)(lightNeutralLayerL2, subtract(darkNeutralLayerL4, multiply(neutralFillCardDelta, 2)))));
/**
 * Used as the background for secondary command surfaces, logically below L2.
 */
export const neutralLayerL3 = colorRecipeFactory(luminanceOrBackgroundColor(getSwatch(add(neutralLayerL2Index, neutralFillCardDelta), neutralPalette), swatchByMode(neutralPalette)(add(lightNeutralLayerL2, neutralFillCardDelta), subtract(darkNeutralLayerL4, neutralFillCardDelta))));
/**
 * Used as the background for the lowest command surface or title bar, logically below L3.
 */
export const neutralLayerL4 = colorRecipeFactory(luminanceOrBackgroundColor(getSwatch(add(neutralLayerL2Index, multiply(neutralFillCardDelta, 2)), neutralPalette), swatchByMode(neutralPalette)(add(lightNeutralLayerL2, multiply(neutralFillCardDelta, 2)), darkNeutralLayerL4)));
